import confetti from "canvas-confetti";
import { LoadingButton } from "./loadingButton";
import { resetTurnstile } from "./turnstile";
import { delay, showFadedOutContainer } from "./utils";
/**
 * Initialize the breach timeline functionality
 */
export function initEmailSearch() {
    // Email search functionality
    const pwnedSearchForm = document.getHtmlElementById("pwnedSearch");
    const emailInput = document.getHtmlElementById("emailInput");
    const checkButton = document.getHtmlElementById("checkButton");
    const breachTimeline = document.getHtmlElementById("breachTimeline");
    // Result state elements
    const goodResultElement = document.getHtmlElementById("email-result-good");
    const badResultElement = document.getHtmlElementById("email-result-bad");
    const regularBreachSummary = document.getHtmlElementById("regular-breach-summary");
    // Set up result elements if they exist
    if (goodResultElement && badResultElement) {
        // Make sure the original breach summary is hidden
        if (regularBreachSummary) {
            regularBreachSummary.bootstrapHide();
        }
    }
    if (pwnedSearchForm && emailInput && checkButton && breachTimeline) {
        emailInput.addEventListener("input", function () {
            if (this.value.trim() !== "") {
                this.classList.remove("is-invalid");
            }
        });
        checkButton.addEventListener("click", async (e) => {
            try {
                e.preventDefault(); // Prevent default form submission
                const email = emailInput.value.trim();
                if (!isValidEmail(email)) {
                    // Show validation error if email is empty
                    emailInput.classList.add("is-invalid");
                    return;
                }
                // Hide any previously shown results with animation
                await hideResultsWithAnimation();
                const loadingButton = LoadingButton.getOrCreateInstance(checkButton);
                loadingButton.start();
                const searchResults = await performSearch(email);
                if (!searchResults.Breaches) {
                    // If no breaches are found, set it to an empty array
                    searchResults.Breaches = [];
                }
                if (!searchResults.Pastes) {
                    // If no pastes are found, set it to an empty array
                    searchResults.Pastes = [];
                }
                renderBreaches(searchResults);
                renderPastes(searchResults);
                loadingButton.stop();
                // Clear any previous validation errors
                emailInput.classList.remove("is-invalid");
                // Show success or warning result based on breach count
                if (searchResults.Breaches.length === 0) {
                    await showGoodResult();
                }
                else {
                    await showBadResult();
                }
                // Show the breach timeline section
                breachTimeline.bootstrapShow();
                // Scroll to the breach timeline section
                breachTimeline.scrollIntoView({ behavior: "smooth" });
                if (searchResults.Breaches.length === 0) {
                    // Trigger confetti animation with a slight delay for better visual effect
                    await delay(500);
                    triggerConfetti();
                }
            }
            finally {
                resetTurnstile();
            }
        });
    }
    // NotifyMe button functionality
    const notifyMeBtn = document.getHtmlElementById("notifyMeBtn");
    if (notifyMeBtn && emailInput) {
        notifyMeBtn.addEventListener("click", () => {
            // Get the email from the search input
            const email = emailInput.value.trim();
            if (email) {
                // Store the email in session storage for use on the notifications page
                sessionStorage.setItem("searchedEmail", email);
            }
        });
    }
    // Function to hide result elements with animation
    async function hideResultsWithAnimation() {
        if (goodResultElement && badResultElement) {
            // If neither result is visible, no need for animation
            if (!goodResultElement.isBootstrapVisible() && !badResultElement.isBootstrapVisible()) {
                return;
            }
            // If the good result is not visible, show the bad result
            if (!goodResultElement.isBootstrapVisible()) {
                await showFadedOutContainer(badResultElement);
                await badResultElement.whenQueriedHtmlElementExists(".search-result", async (resultContainer) => {
                    resultContainer.classList.add("fade-out");
                });
            }
            else if (!badResultElement.isBootstrapVisible()) {
                // If the bad result is not visible, fade out the good result
                await showFadedOutContainer(goodResultElement);
                await goodResultElement.whenQueriedHtmlElementExists(".search-result", async (resultContainer) => {
                    resultContainer.classList.add("fade-out");
                });
            }
            await delay(400);
            // Hide elements after fade-out completes
            goodResultElement.bootstrapHide();
            badResultElement.bootstrapHide();
        }
    }
    // Function to show good result with animation
    async function showGoodResult() {
        if (goodResultElement && badResultElement) {
            // First ensure the element is in DOM but not visible
            badResultElement.bootstrapHide();
            goodResultElement.bootstrapShow();
            // Fade-out the container if it exists
            await goodResultElement.whenQueriedHtmlElementExists(".search-result", async (resultContainer) => {
                await showFadedOutContainer(resultContainer);
            });
        }
    }
    // Function to show bad result with animation
    async function showBadResult() {
        if (goodResultElement && badResultElement) {
            // First ensure the element is in DOM but not visible
            goodResultElement.bootstrapHide();
            badResultElement.bootstrapShow();
            // Fade-out the container if it exists
            await badResultElement.whenQueriedHtmlElementExists(".search-result", async (resultContainer) => {
                await showFadedOutContainer(resultContainer);
            });
        }
    }
    // Function to trigger confetti animation
    async function triggerConfetti() {
        // First, create an initial burst of confetti
        confetti({
            particleCount: 100,
            spread: 70,
            origin: { y: 0.6 },
        });
        await delay(250);
        // Then trigger a cannon from both sides
        confetti({
            particleCount: 50,
            angle: 60,
            spread: 55,
            origin: { x: 0 },
        });
        confetti({
            particleCount: 50,
            angle: 120,
            spread: 55,
            origin: { x: 1 },
        });
    }
}
function renderBreaches(searchResults) {
    const breachCountElement = document.getHtmlElementById("breachCount");
    const breachCountDescription = document.getHtmlElementById("breachCountDescription");
    const breachCountLabelElement = document.getHtmlElementById("breachCountLabel");
    const timelineItems = document.getHtmlElementById("timelineItems");
    if (breachCountElement && breachCountLabelElement && breachCountDescription && timelineItems) {
        // Update the breach count in the bad result element
        breachCountElement.innerHTML = searchResults.Breaches.length.toLocaleString();
        breachCountLabelElement.innerHTML = searchResults.Breaches.length === 1 ? "Data Breach" : "Data Breaches";
        breachCountDescription.innerHTML = searchResults.Breaches.length === 1 ? "a data breach" : "multiple data breaches";
        // Sort results by breach date descending
        searchResults.Breaches.sort((a, b) => Date.parse(b.BreachDate) - Date.parse(a.BreachDate));
        // Only render the timeline items if there are breaches
        if (searchResults.Breaches.length > 0) {
            let timelineItemsContent = "";
            for (const breach of searchResults.Breaches) {
                const breachDate = new Date(Date.parse(breach.BreachDate));
                let dataClassContent = "";
                for (const dataClass of breach.DataClasses) {
                    dataClassContent += `<li class="d-flex align-items-start mb-2">
                               <i class="bi bi-dot me-2 text-primary"></i>${dataClass}
                             </li>`;
                }
                timelineItemsContent += `<div class="timeline-item pt-5">
                                      <div class="timeline-date d-flex flex-column justify-content-center align-items-center text-white">
                                          <span class="timeline-date-text">${breachDate.toLocaleDateString("default", { month: "short" })}</span>
                                          <span class="timeline-date-text">${breachDate.toLocaleDateString("default", { year: "numeric" })}</span>
                                      </div>
                                      <div class="timeline-content p-4 rounded-3 z-2">
                                          <div class="timeline-title d-flex align-items-center mb-3">
                                              <div class="timeline-logo me-3 rounded-2 d-flex align-items-center justify-content-center overflow-hidden position-relative">
                                                  <img src="${`https://haveibeenpwned.com${new URL(breach.LogoPath).pathname}`}"
                                                       alt="${breach.Name} Logo"
                                                       class="w-100 h-100 p-1" />
                                              </div>
                                              <h5 class="mb-0 me-auto fw-semibold text-white">${breach.Title}</h5>
                                          </div>
                                          <div class="mt-2">
                                              <p class="mb-2">${breach.Description}</p>

                                              <div class="mb-3">
                                                  <strong>Compromised data:</strong>
                                                  <ul class="timeline-details-list mt-2 list-unstyled mb-0 p-3 rounded-3 bg-dark-subtle-custom">
                                                      ${dataClassContent}
                                                  </ul>
                                              </div>

                                              <div class="d-flex justify-content-between align-items-center">
                                                  <a href="breach/${breach.Name}"
                                                     class="btn btn-outline-primary">
                                                      View Details
                                                  </a>
                                              </div>
                                          </div>
                                      </div>
                                  </div>`;
            }
            timelineItems.innerHTML = timelineItemsContent;
        }
        else {
            // Clear the timeline if no breaches
            timelineItems.innerHTML = "";
        }
    }
}
function renderPastes(searchResults) {
    const pasteSummaryElement = document.getElementById("pasteSummary");
    const pasteListElement = document.getElementById("pasteList");
    const pasteCountElement = document.getElementById("pasteCount");
    const pasteTableElement = document.getElementById("pasteTable");
    // pastes
    if (pasteSummaryElement && pasteListElement && pasteCountElement && pasteTableElement) {
        if (searchResults.Pastes && searchResults.Pastes.length > 0) {
            let pasteList = "";
            for (const pasteItem of searchResults.Pastes) {
                const pasteDate = pasteItem.Date ? new Date(Date.parse(pasteItem.Date)) : null;
                let pasteClass = "bi-file-earmark-text";
                if (pasteItem.Source === "Pastebin") {
                    pasteClass = "bi-file-earmark-text";
                }
                pasteList += `<tr>
                        <td>
                          <div class="d-flex align-items-center">
                            <div class="table-row-icon me-3">
                              <i class="bi ${pasteClass}"></i>
                            </div>
                            <div>${pasteItem.Title}</div>
                          </div>
                        </td>
                        <td>${pasteDate?.toLocaleDateString("default", { month: "long", day: "numeric", year: "numeric" }) ?? "-"}</td>
                        <td class="text-end">${new Intl.NumberFormat().format(pasteItem.EmailCount)}</td>
                      </tr>`;
            }
            pasteSummaryElement.bootstrapShow();
            pasteListElement.bootstrapShow();
            if (pasteCountElement) {
                pasteCountElement.innerHTML = searchResults.Pastes.length.toLocaleString();
            }
            if (pasteTableElement) {
                pasteTableElement.innerHTML = pasteList;
            }
        }
        else {
            pasteSummaryElement.bootstrapHide();
            pasteListElement.bootstrapHide();
        }
    }
}
function isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
}
async function performSearch(email) {
    // Fetch the breach data
    const apiEndpointElement = document.getHtmlElementById("apiEndpoint");
    const turnstileTokenElement = document.getHtmlElementById("turnstileWidget");
    const form = document.getElementById("pwnedSearch");
    try {
        if (apiEndpointElement && turnstileTokenElement) {
            const path = apiEndpointElement.value + encodeURIComponent(email);
            const turnstileToken = turnstileTokenElement.getAttribute("data-token") ?? "";
            console.log("Beginnning API request");
            const response = await fetch(path, {
                headers: { "cf-turnstile-response": turnstileToken },
            });
            if (response.ok) {
                const data = (await response.json());
                console.log("HTTP 200 returned");
                return data;
            }
            if (response.status === 404) {
                console.log("HTTP 404 returned");
                return {
                    Breaches: [],
                    Pastes: [],
                };
            }
            console.log("Neither HTTP 200 nor 404 returned");
            throw new Error(`Invalid response from fetch: ${response.status} - ${response.statusText}`);
        }
        throw new Error("Required elements not found on page.");
    }
    catch (err) {
        console.log("Falling into catch statement");
        if (form) {
            console.log("Now doing full page post back");
            form.submit();
        }
        throw err;
    }
    finally {
        console.log("Falling into finally statement");
        resetTurnstile();
    }
}
